import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { ButtonLink } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ErrorState, LoadingState, StateBlock } from '@/components/ui/StateBlock';
import { Surface } from '@/components/ui/Surface';
import { TableSkeleton } from '@/components/ui/TableSkeleton';
import { Pagination } from '@/components/ui/Pagination';
import { useCurrentUserQuery } from '@/features/user/queries';
import { describeError } from '@/lib/http';

import { fetchChatGptResult, fetchDirectoryUsers, type Transcription } from '../api';
import { sanitizeChatGptHtmlClient } from '../chatgptHtml';
import {
  TranscriptionsFilters,
  type ListFiltersState,
} from '../components/TranscriptionsFilters';
import { TranscriptionsTable } from '../components/TranscriptionsTable';
import { EditMeetingModal } from '../components/EditMeetingModal';
import { usePurgeTranscriptionMutation, useTranscriptionsQuery } from '../queries';

const SEARCH_DEBOUNCE_MS = 300;

function filtersFromParams(params: URLSearchParams): ListFiltersState {
  return {
    search: params.get('search') ?? '',
    clientName: params.get('clientName') ?? '',
    axiumParticipantId: params.get('axiumParticipantId') ?? '',
    meetingType: params.get('meetingType') ?? '',
    status: params.get('status') ?? '',
  };
}

function paramsFromFilters(filters: ListFiltersState, page: number): URLSearchParams {
  const next = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value.trim() !== '') {
      next.set(key, value.trim());
    }
  }
  if (page > 1) {
    next.set('page', String(page));
  }
  return next;
}

function hasActiveFilters(filters: ListFiltersState): boolean {
  return Object.values(filters).some((value) => value.trim() !== '');
}

export function TranscriptionsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const [localSearch, setLocalSearch] = useState(filters.search);
  const searchTimeoutRef = useRef<number | null>(null);
  const currentPage = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const query = useTranscriptionsQuery({ ...filters, page: currentPage });
  const currentUser = useCurrentUserQuery();
  const purgeMutation = usePurgeTranscriptionMutation();
  const isAdmin = currentUser.data?.role === 'ADMIN';
  const usersQuery = useQuery({
    queryKey: ['directory-users'],
    queryFn: ({ signal }) => fetchDirectoryUsers(signal),
    staleTime: 60_000,
  });
  const page = query.data;
  const [editing, setEditing] = useState<Transcription | null>(null);
  const [viewing, setViewing] = useState<{
    transcriptionId: string;
    resultId: string;
    title: string;
  } | null>(null);
  const [viewHtml, setViewHtml] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (localSearch === filters.search) {
      return;
    }

    searchTimeoutRef.current = window.setTimeout(() => {
      const nextFilters = { ...filters, search: localSearch };
      setSearchParams(paramsFromFilters(nextFilters, 1), { replace: true });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearch, filters, setSearchParams]);

  useEffect(() => {
    if (viewing === null) {
      setViewHtml(null);
      setViewError(null);
      setViewLoading(false);
      return;
    }
    let cancelled = false;
    setViewLoading(true);
    setViewError(null);
    void fetchChatGptResult(viewing.transcriptionId, viewing.resultId)
      .then((result) => {
        if (!cancelled) {
          setViewHtml(result.contentHtml);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setViewError(describeError(cause));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setViewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [viewing]);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handleFiltersChange = useCallback(
    (next: ListFiltersState) => {
      setLocalSearch(next.search);
      setSearchParams(paramsFromFilters(next, 1), { replace: true });
    },
    [setSearchParams],
  );

  const handleFiltersReset = useCallback(() => {
    setLocalSearch('');
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams(paramsFromFilters(filters, newPage), { replace: true });
    },
    [filters, setSearchParams],
  );

  const handleDelete = useCallback(
    (id: string, title: string) => {
      const confirmed = window.confirm(
        `Удалить встречу «${title}» безвозвратно?\nФайлы и расшифровка будут удалены.`,
      );
      if (!confirmed) {
        return;
      }
      purgeMutation.mutate(id, {
        onError: (error) => {
          window.alert(`Не удалось удалить: ${describeError(error)}`);
        },
      });
    },
    [purgeMutation],
  );

  const isFiltered = hasActiveFilters(filters);
  const showingResults = page !== undefined && page.items.length > 0;
  const counterLabel =
    page === undefined
      ? undefined
      : isFiltered
        ? `Найдено: ${page.total}`
        : page.total > 0
          ? `Всего: ${page.total}`
          : undefined;

  return (
    <>
      <PageHeader
        title="Журнал встреч"
        description="Пресейлы, клиентские и внутренние встречи AXIUM."
        actions={<ButtonLink to="/transcriptions/new">Добавить встречу</ButtonLink>}
      />

      <Surface
        flush
        actions={
          counterLabel !== undefined ? (
            <span className="data-table__muted">{counterLabel}</span>
          ) : undefined
        }
      >
        <TranscriptionsFilters
          value={{ ...filters, search: localSearch }}
          users={usersQuery.data ?? []}
          onChange={handleFiltersChange}
          onSearchChange={handleSearchChange}
          onReset={handleFiltersReset}
        />

        {query.isPending ? <TableSkeleton rows={5} /> : null}

        {query.isError ? (
          <ErrorState
            description="Сервис не ответил. Проверьте, что API доступен, и повторите запрос."
            details={describeError(query.error)}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : null}

        {page !== undefined && page.items.length === 0 ? (
          <StateBlock
            icon="∅"
            title={isFiltered ? 'По вашему запросу ничего не найдено' : 'Встреч пока нет'}
            description={
              isFiltered
                ? 'Измените параметры поиска или сбросьте фильтры.'
                : 'Добавьте встречу, чтобы сохранить расшифровку в журнале.'
            }
            action={
              isFiltered ? (
                <button type="button" className="button" onClick={handleFiltersReset}>
                  Сбросить фильтры
                </button>
              ) : (
                <ButtonLink to="/transcriptions/new">Добавить встречу</ButtonLink>
              )
            }
          />
        ) : null}

        {showingResults ? (
          <>
            <TranscriptionsTable
              items={page.items}
              currentUserId={currentUser.data?.id}
              isAdmin={isAdmin}
              deletingId={purgeMutation.isPending ? purgeMutation.variables ?? null : null}
              onDelete={handleDelete}
              onEdit={setEditing}
              onViewResult={(meeting) => {
                if (meeting.latestChatGptResult === null) {
                  return;
                }
                setViewing({
                  transcriptionId: meeting.id,
                  resultId: meeting.latestChatGptResult.id,
                  title: meeting.latestChatGptResult.title,
                });
              }}
            />
            <Pagination
              currentPage={page.page}
              totalPages={page.totalPages}
              totalItems={page.total}
              pageSize={page.pageSize}
              onPageChange={handlePageChange}
            />
          </>
        ) : null}
      </Surface>

      <EditMeetingModal
        open={editing !== null}
        meeting={editing}
        onClose={() => setEditing(null)}
      />

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing?.title ?? 'Результат'}
        width="920px"
      >
        {viewLoading ? <LoadingState title="Загрузка результата…" /> : null}
        {viewError !== null ? <ErrorState description={viewError} /> : null}
        {viewHtml !== null ? (
          <div
            className="chatgpt-result-view__html"
            dangerouslySetInnerHTML={{ __html: sanitizeChatGptHtmlClient(viewHtml) }}
          />
        ) : null}
      </Modal>
    </>
  );
}
